/**
 * InvoiceForm.js
 * Formulário inline para criar ou editar um faturamento de agendamento.
 * Props:
 *   appointment  — objeto do agendamento
 *   invoiceData  — dados do invoice existente (null em modo create)
 *   mode         — 'create' | 'edit'
 *   onSuccess    — callback chamado após salvar/excluir com sucesso
 *   onCancel     — callback para fechar o formulário sem salvar
 *
 * Sem forma de pagamento cadastrada (modo create, proprietário ou admin), o
 * formulário abre primeiro o cadastro da primeira forma (mesmo formulário da
 * tela de métodos) e, ao salvar, segue para o faturamento com ela selecionada.
 *
 * Em modo create mostra uma linha por procedimento (InvoiceLines): cada uma com
 * pacote (desconta uma sessão) ou avulsa (soma no valor). Fatura em uma chamada
 * (createInvoiceWithLines); só de pacote grava paymentMethod 'credits' e valor 0.
 *
 * `copay` (atendimento por convênio): registra só o que a pessoa pagou. Sem as
 * linhas com o preço particular, valor em branco e nenhuma sessão de pacote.
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { DataProviderInstance } from '../../../../../api/DataProvider';
import { createInvoiceWithLines } from '../../../../../api/AppointmentBillingAPI';
import { parseMoney, formatMoneyInput, cleanMoneyTyping } from '../../../../../utils/moneyUtils';
import { useRoleAccess } from '../../../../../hooks/useRoleAccess';
import useSubscriptionFeatures from '../../../../../hooks/useSubscriptionFeatures';
import { SUBSCRIPTION_PERMISSIONS } from '../../../../../consts/SubscriptionFeatures';
import { USER_ROLES } from '../../../../../constants/userRoles';
import PaymentMethodForm from '../../../../../components/payment-methods/components/PaymentMethodForm';
import { usePaymentMethodForm } from '../../../../../components/payment-methods/components/usePaymentMethodForm';
import { TrashIcon } from './PaymentSectionIcons';
import InvoiceLines from './InvoiceLines';
import useBillingLines from './useBillingLines';
import styles from './AppointmentDetailPanel.module.css';

/**
 * `sessionsUsed` (só em edit): sessões de pacote descontadas neste pagamento
 * ([{procedure_id, credit_id}]). A edição não mexe nelas: para trocar, a pessoa
 * exclui o pagamento (as sessões voltam ao saldo) e fatura de novo.
 */
export default function InvoiceForm({ appointment, invoiceData, mode, sessionsUsed = [], copay = false, onSuccess, onCancel }) {
  const { CompanyConfig, UserData, session, UpdateInvoice, DeleteInvoice, SetCompanyPaymentMethod } = DataProviderInstance();
  const { hasRole } = useRoleAccess();
  const opts = CompanyConfig?.PaymentOptions || [];
  const defMethod = opts[0]?.Id || '';

  // Linhas da fatura (só em create): pacote x avulso por procedimento.
  // Plano sem pacotes: tudo avulso, opção de pacote com cadeado (nunca escondida).
  const { hasPermission } = useSubscriptionFeatures();
  const packagesLocked = !hasPermission(SUBSCRIPTION_PERMISSIONS.SERVICE_PACKAGES);
  const billing = useBillingLines(appointment, { enabled: mode === 'create', locked: packagesLocked || copay });
  const { directTotal } = billing.summary;
  // Coparticipação nunca é "só de pacote": sem linhas, sempre pede o valor
  const allPackage = billing.summary.allPackage && !copay;

  // Primeira forma de pagamento cadastrada pelo próprio diálogo (só quem pode gerir métodos)
  const canCreateMethod = hasRole([USER_ROLES.COMPANY_OWNER, USER_ROLES.COMPANY_ADMIN]);
  const methodForm = usePaymentMethodForm();
  const [creatingMethod, setCreatingMethod] = useState(false);
  const showQuickCreate = mode === 'create' && opts.length === 0 && canCreateMethod;

  // Retorna o PriceModifier de um método de pagamento (pode ser positivo ou negativo)
  const getModifier = (id) => {
    if (!id || id === 'credits') return 0;
    return Number(CompanyConfig?.PaymentOptions?.find(o => String(o.Id) === String(id))?.PriceModifier || 0);
  };

  // Calcula preço base + modifier
  const calcPrice = (base, id) => {
    const b = Number(base) || 0;
    const mod = getModifier(id);
    return b > 0 ? +(b + mod).toFixed(2) : b;
  };

  const [price,    setPrice]    = useState('');
  const [method,   setMethod]   = useState(defMethod);
  const [issueNf,  setIssueNf]  = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirm,  setConfirm]  = useState(false);
  const [err,      setErr]      = useState('');
  const [lineErr,  setLineErr]  = useState(''); // erro do backend ao faturar (rodapé das linhas)

  useEffect(() => {
    if (mode === 'edit' && invoiceData) {
      // Em edição: carrega o preço já salvo e o método salvo (sem reaplicar modifier)
      setPrice(Number(invoiceData.price) > 0 ? formatMoneyInput(invoiceData.price) : '');
      setMethod(invoiceData.paymentMethod || defMethod);
      setIssueNf(invoiceData.issueDocument === '1' || invoiceData.issueDocument === 1);
    } else {
      setMethod(defMethod);
      setIssueNf(false);
    }
  }, [mode, invoiceData]); // eslint-disable-line

  // Em criação o valor segue a soma dos avulsos mais o modifier do método
  // (a pessoa ainda pode alterar o campo antes de faturar)
  useEffect(() => {
    if (mode !== 'create' || copay) return;
    const next = calcPrice(directTotal, method);
    setPrice(next > 0 ? formatMoneyInput(next) : '');
  }, [mode, directTotal, method]); // eslint-disable-line

  const handleMethodChange = (newMethod) => setMethod(newMethod);

  // A forma recém-cadastrada chega pelo CompanyConfig: seleciona e aplica o ajuste no valor
  useEffect(() => {
    if (mode === 'create' && !method && opts.length > 0) handleMethodChange(opts[0].Id);
  }, [opts.length]); // eslint-disable-line

  const createMethod = () => {
    if (!methodForm.validate()) return;
    setErr(''); setCreatingMethod(true);
    SetCompanyPaymentMethod(methodForm.payload(), (res) => {
      setCreatingMethod(false);
      if (res?.error) setErr('Não foi possível cadastrar a forma de pagamento. Tente novamente.');
    });
  };

  // Considera sucesso se não houver campo "error" na resposta
  const isSuccess = (res) => !res?.error;

  const save = () => {
    // Formato brasileiro: "1.200,99" grava 1200.99, "1.200" grava 1200, "170,50" grava 170.50
    const n = parseMoney(price);
    // Só de pacote: nada a cobrar, sem forma de pagamento
    const onlyPackages = mode === 'create' && allPackage;
    if (!onlyPackages) {
      if (n === null || n <= 0) { setErr('Informe um valor válido, como 150,00 ou 1.200,99.'); return; }
      if (!method) { setErr('Escolha a forma de pagamento.'); return; }
    }
    setErr(''); setLineErr(''); setSaving(true);
    if (mode === 'create') {
      createInvoiceWithLines({
        appointmentId: appointment.id,
        companyId: session?.currentCompany ?? CompanyConfig?.Id ?? '',
        createdBy: UserData?.id,
        patient: appointment.patient,
        price: onlyPackages ? 0 : n,
        paymentMethod: onlyPackages ? 'credits' : method,
        procedure: appointment.procedure,
        issueDocument: !onlyPackages && issueNf ? 1 : 0,
        lines: copay ? null : billing.payloadLines,
      }, (res) => {
        setSaving(false);
        if (res?.status === 'SUCCESS') {
          // onSuccess chega ao onInvoiceCreated da agenda, que já recarrega o período da tela
          onSuccess?.();
          return;
        }
        // Mensagem do backend no rodapé das linhas; pacote sem saldo (409
        // NO_CREDITS) recarrega as linhas com o saldo real
        setLineErr(res?.message || res?.error || 'Erro ao criar faturamento.');
        if (res?.httpStatus === 409 || res?.code === 'NO_CREDITS') billing.reload();
      });
    } else {
      UpdateInvoice(invoiceData.id, n, method, issueNf ? 1 : 0, (res) => {
        setSaving(false);
        if (isSuccess(res)) onSuccess?.();
        else setErr(res?.message || res?.error || 'Erro ao atualizar faturamento.');
      });
    }
  };

  const del = () => {
    setDeleting(true);
    DeleteInvoice(invoiceData.id, (res) => {
      setDeleting(false); setConfirm(false);
      if (isSuccess(res)) onSuccess?.();
      else setErr(res?.message || res?.error || 'Erro ao excluir faturamento.');
    });
  };

  if (showQuickCreate) {
    return (
      <div className={styles.invoiceForm}>
        <p className={styles.invoiceFormIntro}>
          Nenhuma forma de pagamento cadastrada ainda. Cadastre a primeira aqui e siga para o faturamento.
        </p>
        <PaymentMethodForm formData={methodForm.formData} onChange={methodForm.handleChange} errors={methodForm.errors} />
        {err && <div className={styles.invoiceFormError}>{err}</div>}
        <div className={styles.invoiceFormActions}>
          <button type="button" className={styles.invoiceFormBtnCancel}
            onClick={onCancel} disabled={creatingMethod}>
            Cancelar
          </button>
          <button type="button" className={styles.invoiceFormBtnSave}
            onClick={createMethod} disabled={creatingMethod}>
            {creatingMethod && <span className={styles.spinner} />}
            {creatingMethod ? 'Cadastrando...' : 'Cadastrar e continuar'}
          </button>
        </div>
        <span className={styles.invoiceFormHint}>
          Prefere a tela completa? <Link to="/company/payment-methods">Formas de pagamento</Link>
        </span>
      </div>
    );
  }

  // Só de pacote: sem valor, forma de pagamento nem nota fiscal
  // (em edição, o pagamento gravado como 'credits' só pode ser excluído)
  const creditsOnly = mode === 'edit' && invoiceData?.paymentMethod === 'credits';
  const showCharge = !(mode === 'create' && allPackage) && !creditsOnly;

  // Em edição: lembrete das sessões já descontadas (não mudam por aqui)
  const sessionsCount = mode === 'edit' ? sessionsUsed.length : 0;
  const sessionsNames = sessionsCount
    ? sessionsUsed
      .map(s => (CompanyConfig?.Procedures || []).find(p => String(p.Id) === String(s.procedure_id))?.Label)
      .filter(Boolean)
      .join(', ')
    : '';
  const sessionsWord = sessionsCount === 1 ? '1 sessão de pacote descontada' : `${sessionsCount} sessões de pacote descontadas`;

  return (
    <div className={styles.invoiceForm}>
      {sessionsCount > 0 && (
        <p className={styles.invoiceFormIntro}>
          <strong>{sessionsWord}</strong>{sessionsNames ? `: ${sessionsNames}` : ''}.
          {' '}Para mudar as sessões, exclua o pagamento (elas voltam ao saldo) e fature de novo.
        </p>
      )}
      {mode === 'create' && copay && (
        <p className={styles.invoiceFormIntro}>
          Informe só o valor que a pessoa pagou. O que a operadora paga não entra aqui.
        </p>
      )}
      {mode === 'create' && !copay && (
        <InvoiceLines
          lines={billing.lines}
          choices={billing.choices}
          onChange={(procedureId, source) => { billing.setChoice(procedureId, source); setErr(''); setLineErr(''); }}
          locked={packagesLocked}
          lockAction={<Link to="/subscription/plans">Ver planos</Link>}
          loading={billing.loading}
          disabled={saving}
          error={lineErr}
        />
      )}
      {showCharge && (
        <>
          <div className={styles.invoiceFormRow} data-tour={mode === 'create' ? 'payment-price' : undefined}>
            <label className={styles.invoiceFormLabel}>Valor (R$)</label>
            <input
              type="text"
              inputMode="decimal"
              className={styles.invoiceFormInput}
              value={price}
              onChange={e => { setPrice(cleanMoneyTyping(e.target.value)); setErr(''); }}
              onBlur={() => { const n = parseMoney(price); if (n !== null && n > 0) setPrice(formatMoneyInput(n)); }}
              placeholder="0,00"
              disabled={saving || deleting}
            />
          </div>
          <div className={styles.invoiceFormRow} data-tour={mode === 'create' ? 'payment-method' : undefined}>
            <label className={styles.invoiceFormLabel}>Método de pagamento</label>
            {opts.length === 0 && (
              <span className={styles.invoiceFormError}>
                Nenhuma forma de pagamento cadastrada. <Link to="/company/payment-methods">Cadastrar formas de pagamento</Link>
              </span>
            )}
            <select
              className={styles.invoiceFormSelect}
              value={method}
              onChange={e => handleMethodChange(e.target.value)}
              disabled={saving || deleting || opts.length === 0}
            >
              {opts.map(o => {
                const mod = Number(o.PriceModifier || 0);
                const modStr = mod > 0
                  ? ` (+R$ ${mod.toFixed(2).replace('.', ',')})`
                  : mod < 0
                    ? ` (-R$ ${Math.abs(mod).toFixed(2).replace('.', ',')})`
                    : '';
                return <option key={o.Id} value={o.Id}>{o.Label}{modStr}</option>;
              })}
            </select>
          </div>
          <label className={styles.invoiceFormCheckRow}>
            <input
              type="checkbox" className={styles.invoiceFormCheckbox}
              checked={issueNf}
              onChange={e => setIssueNf(e.target.checked)}
              disabled={saving || deleting}
            />
            <span className={styles.invoiceFormCheckLabel}>Marcar para emissão de nota fiscal</span>
          </label>
        </>
      )}
      {err && <div className={styles.invoiceFormError}>{err}</div>}
      {copay && lineErr && <div className={styles.invoiceFormError}>{lineErr}</div>}
      <div className={styles.invoiceFormActions}>
        {mode === 'edit' && !confirm && (
          <button type="button" className={styles.invoiceFormBtnDelete}
            onClick={() => setConfirm(true)} disabled={saving || deleting}>
            <TrashIcon /><span>Excluir</span>
          </button>
        )}
        {confirm && (
          <div className={styles.invoiceDeleteConfirm}>
            <span className={styles.invoiceDeleteConfirmText}>
              {sessionsCount > 0
                ? `Excluir faturamento? ${sessionsCount === 1 ? 'A sessão de pacote volta' : 'As sessões de pacote voltam'} ao saldo.`
                : 'Excluir faturamento?'}
            </span>
            <button type="button" className={styles.invoiceFormBtnDeleteConfirm}
              onClick={del} disabled={deleting}>
              {deleting && <span className={styles.spinner} />}
              {deleting ? 'Excluindo...' : 'Confirmar'}
            </button>
            <button type="button" className={styles.invoiceFormBtnCancel}
              onClick={() => setConfirm(false)} disabled={deleting}>
              Voltar
            </button>
          </div>
        )}
        {!confirm && (
          <>
            <button type="button" className={styles.invoiceFormBtnCancel}
              onClick={onCancel} disabled={saving || deleting}>
              Cancelar
            </button>
            {!creditsOnly && (
              <button type="button" className={styles.invoiceFormBtnSave}
                data-tour={mode === 'create' ? 'payment-save' : undefined}
                onClick={save}
                disabled={saving || deleting || (showCharge && !method) || (mode === 'create' && billing.loading)}>
                {saving && <span className={styles.spinner} />}
                {saving ? 'Salvando...' : mode === 'create' ? 'Faturar' : 'Salvar'}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
